import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Plus, Printer, ScanLine, XCircle } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { dateTime, humanize } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { fallbackInterval, useRealtimeInvalidate } from '@/lib/realtime';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { ActionMenu, ActionMenuItem, ActionMenuSeparator, Badge, Button, Checkbox, Input, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { OrderResultsSheet } from './OrderResultsSheet';
import { LabelPrintDialog, type LabelTarget } from './LabelPrintDialog';
import { LabBenchAddTestsDialog } from './LabBenchAddTestsDialog';
import { apiMessage, hasDetailPath, isConflict, messageMentions } from './LabBenchErrors';
import { LabBenchReasonDialog } from './LabBenchReasonDialog';
import { LabRejectDialog } from './LabRejectDialog';
import { LabApprovalsPage } from './LabApprovalsPage';
import { LabSendOutDialog } from './LabSendOutDialog';
import { LabIdentityDialog, type CollectionIdentity, type IdentityPatient } from './LabIdentityDialog';
import { SOURCE_LABELS, groupTubes, locationText, type BenchLocation, type TubeGroup } from './bench-groups';

interface WorklistItem {
  id: string;
  accessionNo: string | null;
  status: string;
  overdue: boolean;
  statOverdue?: boolean;
  resultHidden?: boolean;
  labOrderId: string;
  orderNumber: string;
  priority: string;
  orderedAt: string;
  collectedAt: string | null;
  collectAt: string | null;
  referringDoctorName: string | null;
  homeCollection: boolean;
  source: string;
  location: BenchLocation | null;
  enteredBy: string | null;
  unpaid?: boolean;
  awaitingPayment?: boolean;
  awaitingInsurer?: boolean;
  sentOutTo?: string | null;
  test: { id: string; code: string; name: string; departmentGroup: string; specimenType: string; containerType: string | null; unit: string | null; resultType: string };
  patient: { id: string; mrn: string; fullName: string; sex: string; dob: string | null };
  latestResult: {
    id: string;
    value: string;
    flag: string;
    unit: string | null;
    referenceText: string | null;
    isAmended?: boolean;
    previousValue?: string | null;
    amendReason?: string | null;
  } | null;
}

interface WorklistPage {
  data: WorklistItem[];
  nextCursor: string | null;
  separateVerifier?: boolean;
  homeCollection?: boolean;
  identityCheck?: boolean;
  identifiers?: string[];
}

interface CollectRequest {
  ids: string[];
  identities?: CollectionIdentity[];
  paymentOverrideReason?: string;
}

interface Locations {
  wards: { id: string; name: string; branchId: string }[];
  branches: { id: string; name: string }[];
}

interface CancelResponse {
  cancelled: number;
  orderCancelled: boolean;
  invoiceLinesRemoved: number;
  refundNeeded: boolean;
  refundInvoiceIds: string[];
}

type VerifyAction = 'verify' | 'release' | 'send_back' | 'preliminary';

type CancelTarget = { kind: 'item' | 'order'; row: WorklistItem };

const CANCELLABLE_STATUSES = ['ordered', 'collected', 'received'];
const TUBE_TABS = new Set(['collect', 'collected']);
const REJECT_TABS = new Set(['collected', 'bench']);

function labelTargets(items: WorklistItem[]): LabelTarget[] {
  return items
    .filter((row) => row.accessionNo)
    .map((row) => ({
      id: row.id,
      accessionNo: row.accessionNo as string,
      patientName: row.patient.fullName,
      mrn: row.patient.mrn,
      testName: row.test.name,
      specimenType: row.test.specimenType,
      containerType: row.test.containerType,
    }));
}

const GROUPS = ['hematology', 'biochemistry', 'microbiology', 'serology', 'histopathology', 'molecular', 'other'];

// The specimen's life, one tab per hand-over: phlebotomy collects, the
// reception desk receives, the bench results, the pathologist releases.
const STATUSES_FOR_TAB: Record<string, string[]> = {
  collect: ['ordered'],
  collected: ['collected'],
  bench: ['received', 'in_process'],
  verify: ['resulted'],
  verified: ['verified'],
  released: ['released'],
};

function PatientCell({ row, location }: { row: WorklistItem; location: BenchLocation | null }) {
  const where = locationText(location);
  return (
    <div>
      <p className="font-medium">
        {row.patient.fullName}
        <Badge variant="secondary" className="ms-1.5 align-middle">
          {SOURCE_LABELS[row.source] ?? humanize(row.source)}
        </Badge>
      </p>
      <p className="text-xs text-muted-foreground">
        {row.patient.mrn}
        {where ? ` · ${where}` : ''}
      </p>
      {row.referringDoctorName && <p className="text-xs text-muted-foreground">{tr('lab', 'ref')} {row.referringDoctorName}</p>}
    </div>
  );
}

function PriorityBadges({ priority, statOverdue, overdue, collectAt }: { priority: string; statOverdue?: boolean; overdue?: boolean; collectAt?: string | null }): ReactNode {
  return (
    <>
      {overdue && (
        <Badge variant="destructive" className="gap-0.5">
          <AlertTriangle className="h-3 w-3" /> {tr('lab', 'tat')}
        </Badge>
      )}
      {statOverdue && (
        <Badge variant="destructive" className="gap-0.5">
          <AlertTriangle className="h-3 w-3" /> {tr('lab', 'stat_not_collected')}
        </Badge>
      )}
      {priority !== 'routine' && <Badge variant="warning">{humanize(priority)}</Badge>}
      {collectAt && <span className="text-xs text-muted-foreground">{tr('lab', 'due')} {dateTime(collectAt)}</span>}
    </>
  );
}

/**
 * Lab bench (SRS 8.1).
 *
 * One screen per stage of the specimen's life, because the people at each stage
 * are different: phlebotomy collects, the bench results, the pathologist
 * releases. Overdue work is flagged against each test's own turnaround target.
 */
export function LabPage() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('collect');
  const [group, setGroup] = useState('');
  const [search, setSearch] = useState('');
  const [referrerDraft, setReferrerDraft] = useState('');
  const [referrer, setReferrer] = useState('');
  const [homeOnly, setHomeOnly] = useState(false);
  const [source, setSource] = useState('');
  const [wardId, setWardId] = useState('');
  const workingBranchId = useWorkingBranchId();
  const [branchPick, setBranchPick] = useState<{ under: string; value: string } | null>(null);
  const branchId = branchPick && branchPick.under === workingBranchId ? branchPick.value : workingBranchId;
  const setBranchId = (value: string) => setBranchPick({ under: workingBranchId, value });
  const [dueNow, setDueNow] = useState(false);
  const [onlyNormal, setOnlyNormal] = useState(false);
  const [awaitingReference, setAwaitingReference] = useState(false);
  const [sendingOut, setSendingOut] = useState<string[] | null>(null);
  const [scan, setScan] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [entry, setEntry] = useState<{ orderId: string; itemId: string } | null>(null);
  const [labelling, setLabelling] = useState<LabelTarget[] | null>(null);
  const [overriding, setOverriding] = useState<{ ids: string[]; action: VerifyAction } | null>(null);
  const [sendingBack, setSendingBack] = useState<string[] | null>(null);
  const [rejecting, setRejecting] = useState<string[] | null>(null);
  const [cancelling, setCancelling] = useState<CancelTarget | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [adding, setAdding] = useState<WorklistItem | null>(null);
  const [identifying, setIdentifying] = useState<{ ids: string[]; patients: IdentityPatient[] } | null>(null);
  const [payingOverride, setPayingOverride] = useState<CollectRequest | null>(null);

  const mayChangeOrders = can('lab.collect', 'lab.order.create', 'order.write');
  const commitReferrer = () => setReferrer(referrerDraft.trim());
  const filtersActive = Boolean(
    group || source || branchId !== workingBranchId || wardId || search || referrer || homeOnly || dueNow || onlyNormal || awaitingReference,
  );
  const resetFilters = () => {
    setGroup('');
    setSource('');
    setBranchPick(null);
    setWardId('');
    setSearch('');
    setReferrerDraft('');
    setReferrer('');
    setHomeOnly(false);
    setDueNow(false);
    setOnlyNormal(false);
    setAwaitingReference(false);
  };

  const locations = useQuery({
    queryKey: ['lab-locations'],
    queryFn: () => get<Locations>('/lab/locations'),
    staleTime: 10 * 60_000,
  });

  const filters = {
    departmentGroup: group || undefined,
    q: search || undefined,
    referrer: referrer || undefined,
    homeCollection: homeOnly ? true : undefined,
    source: source || undefined,
    wardId: wardId || undefined,
    branchId: branchId || undefined,
    dueNow: tab === 'collect' && dueNow ? true : undefined,
    onlyNormal: tab === 'verify' && onlyNormal ? true : undefined,
    sendOut: tab === 'bench' && awaitingReference ? true : undefined,
  };

  const live = useRealtimeInvalidate(
    ['lab-worklist', ...(user?.id ? [`user:${user.id}`] : [])],
    null,
    [['lab-worklist'], ['lab-edit-requests'], ['lab-reprint-requests']],
  );

  const worklist = useInfiniteQuery({
    queryKey: ['lab-worklist', tab, filters],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<WorklistPage>(`/lab/worklist${qs({ statuses: (STATUSES_FOR_TAB[tab] ?? []).join(','), ...filters, cursor: pageParam || undefined, limit: 50 })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: fallbackInterval(live, 60_000),
    enabled: tab !== 'approvals',
  });

  const verifiedWaiting = useQuery({
    queryKey: ['lab-worklist', 'verified-probe'],
    queryFn: () => get<WorklistPage>(`/lab/worklist${qs({ statuses: 'verified', limit: 1 })}`),
    refetchInterval: fallbackInterval(live, 120_000),
    enabled: can('lab.result.verify'),
  });
  const showVerifiedTab = tab === 'verified' || (verifiedWaiting.data?.data.length ?? 0) > 0;

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });

  const collect = useMutation({
    mutationFn: (request: CollectRequest) =>
      post<{ id: string; accessionNo: string }[]>('/lab/collect', {
        orderItemIds: request.ids,
        ...(request.identities ? { identities: request.identities } : {}),
        ...(request.paymentOverrideReason ? { paymentOverrideReason: request.paymentOverrideReason } : {}),
      }),
    onSuccess: (collected) => {
      setPayingOverride(null);
      setIdentifying(null);
      toast.success(tr('lab', 'specimens_collected'), tr('lab', 'accession_numbers_allocated_label_the_tubes'));
      const byId = new Map(collected.map((c) => [c.id, c.accessionNo]));
      setLabelling(labelTargets(rows.filter((row) => byId.has(row.id)).map((row) => ({ ...row, accessionNo: byId.get(row.id) ?? row.accessionNo }))));
      setSelected([]);
      refresh();
    },
    onError: (error, request) => {
      setIdentifying(null);
      if (hasDetailPath(error, 'paymentOverrideReason')) {
        setPayingOverride(request);
        return;
      }
      toast.error(tr('lab', 'could_not_record_collection'), apiMessage(error));
    },
  });

  const startCollect = (ids: string[]) => {
    const chosen = rows.filter((row) => ids.includes(row.id));
    if (pages[0]?.identityCheck) {
      const patients = [...new Map(chosen.map((row) => [row.patient.id, { id: row.patient.id, fullName: row.patient.fullName, mrn: row.patient.mrn }])).values()];
      setIdentifying({ ids, patients });
      return;
    }
    collect.mutate({ ids });
  };

  const receive = useMutation({
    mutationFn: (input: { orderItemIds?: string[]; accessionNo?: string }) => post<{ received: number }>('/lab/receive', input),
    onSuccess: (result, input) => {
      toast.success(input.accessionNo ? tr('lab', 'x_x_test_s_received', { accessionNo: input.accessionNo, received: result.received }) : tr('lab', 'specimens_received_into_the_lab'));
      setSelected([]);
      setScan('');
      refresh();
    },
    onError: (error) => toast.error(tr('lab', 'could_not_receive_specimens'), apiMessage(error)),
  });

  const verify = useMutation({
    mutationFn: ({ ids, action, overrideReason, comment }: { ids: string[]; action: VerifyAction; overrideReason?: string; comment?: string }) =>
      post('/lab/verify', { orderItemIds: ids, action, overrideReason, comment }),
    onSuccess: (_result, variables) => {
      toast.success(
        variables.action === 'send_back'
          ? tr('lab', 'sent_back_to_the_bench')
          : variables.action === 'verify'
            ? tr('lab', 'results_verified')
            : variables.action === 'preliminary'
              ? tr('lab', 'released_as_preliminary')
              : tr('lab', 'results_released'),
      );
      setSelected([]);
      setOverriding(null);
      setSendingBack(null);
      refresh();
    },
    onError: (error, variables) => {
      if (hasDetailPath(error, 'overrideReason')) {
        setOverriding({ ids: variables.ids, action: variables.action });
        return;
      }
      setOverriding(null);
      if (isConflict(error) && messageMentions(error, 'entered by you')) {
        toast.error(
          tr('lab', 'a_different_person_must_verify_these'),
          tr('lab', 'this_lab_requires_results_to_be_verified_by'),
        );
        return;
      }
      if (isConflict(error) && messageMentions(error, 'not fully paid')) {
        toast.error(tr('lab', 'report_held_until_the_bill_is_paid'), apiMessage(error));
        return;
      }
      toast.error(tr('lab', 'could_not_update_the_results'), apiMessage(error));
    },
  });

  const cancel = useMutation({
    mutationFn: ({ target, reason }: { target: CancelTarget; reason: string }) =>
      target.kind === 'item'
        ? post<CancelResponse>(`/lab/orders/${target.row.labOrderId}/cancel-items`, { orderItemIds: [target.row.id], reason })
        : post<CancelResponse>(`/lab/orders/${target.row.labOrderId}/cancel`, { reason }),
    onSuccess: (result, variables) => {
      const wholeOrder = variables.target.kind === 'order' || result.orderCancelled;
      const removed =
        result.invoiceLinesRemoved > 0
          ? `${result.invoiceLinesRemoved} unpaid bill line${result.invoiceLinesRemoved === 1 ? '' : 's'} removed from the invoice`
          : undefined;
      toast.success(wholeOrder ? tr('lab', 'order_x_cancelled', { orderNumber: variables.target.row.orderNumber }) : `${result.cancelled} test${result.cancelled === 1 ? '' : 's'} cancelled`, removed);
      if (result.refundNeeded) {
        toast.warning(tr('lab', 'money_was_already_paid_the_bill_is_marked_for'), tr('lab', 'ask_billing_to_refund_the_patient'));
      }
      setCancelling(null);
      setSelected([]);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['lab-entry'] });
    },
    onError: (error) => setCancelError(apiMessage(error)),
  });

  const printCollectionList = async () => {
    try {
      const doc = await get<{ html: string }>(
        `/lab/collection-list${qs({ wardId: wardId || undefined, branchId: branchId || undefined, source: source || undefined, dueNow: dueNow ? true : undefined })}`,
      );
      printDocument(doc.html);
    } catch (error) {
      toast.error(tr('lab', 'could_not_print_the_collection_list'), apiMessage(error));
    }
  };

  const pages = worklist.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.data);
  const separateVerifier = pages[0]?.separateVerifier === true;
  const homeCollectionOn = pages[0]?.homeCollection !== false;
  const lockedForMe = (row: WorklistItem) => tab === 'verify' && separateVerifier && Boolean(user?.id) && row.enteredBy === user?.id;
  const selectable = rows.filter((row) => !lockedForMe(row));
  const tubes = TUBE_TABS.has(tab) ? groupTubes(rows) : [];
  const toggle = (ids: string[], checked: boolean) =>
    setSelected((current) => (checked ? [...new Set([...current, ...ids])] : current.filter((id) => !ids.includes(id))));
  const allSelected = selectable.length > 0 && selectable.every((row) => selected.includes(row.id));
  const selectedRows = rows.filter((row) => selected.includes(row.id));
  const wards = (locations.data?.wards ?? []).filter((ward) => !branchId || ward.branchId === branchId);

  const actions = (row: WorklistItem, sharedTube = false) => {
    const cancellable = CANCELLABLE_STATUSES.includes(row.status);
    return (
      <div className="flex items-center justify-end gap-1">
        {['collected', 'received', 'in_process', 'resulted'].includes(row.status) && can('lab.result.enter') && (
          <Button size="sm" variant="outline" onClick={() => setEntry({ orderId: row.labOrderId, itemId: row.id })}>
            {row.latestResult ? tr('lab', 'edit_results') : tr('lab', 'enter_results')}
          </Button>
        )}
        {['resulted', 'verified', 'released'].includes(row.status) && !can('lab.result.enter') && (
          <Button size="sm" variant="ghost" onClick={() => setEntry({ orderId: row.labOrderId, itemId: row.id })}>
            {row.status === 'resulted' && can('lab.result.verify') ? tr('lab', 'open_sheet') : tr('lab', 'view_sheet')}
          </Button>
        )}
        {['verified', 'released'].includes(row.status) && can('lab.result.amend') && (
          <Button size="sm" variant="outline" onClick={() => setEntry({ orderId: row.labOrderId, itemId: row.id })}>
            {tr('lab', 'amend_result')}
          </Button>
        )}
        {['verified', 'released'].includes(row.status) && (
          <Button
            size="sm"
            variant="ghost"
            aria-label={tr('lab', 'print_report')}
            onClick={async () => {
              const doc = await get<{ html: string }>(`/lab/orders/${row.labOrderId}/print`);
              printDocument(doc.html);
            }}
          >
            <Printer className="h-4 w-4" />
          </Button>
        )}
        {mayChangeOrders && (
          <ActionMenu label={tr('lab', 'order_actions_for_x', { orderNumber: row.orderNumber })}>
            {cancellable && !sharedTube && (
              <ActionMenuItem
                icon={<XCircle className="h-4 w-4" />}
                onSelect={() => {
                  setCancelError(null);
                  setCancelling({ kind: 'item', row });
                }}
              >
                {tr('lab', 'cancel_test')}
              </ActionMenuItem>
            )}
            <ActionMenuItem icon={<Plus className="h-4 w-4" />} onSelect={() => setAdding(row)}>
              {tr('lab', 'add_tests_to_this_order')}
            </ActionMenuItem>
            {cancellable && (
              <>
                <ActionMenuSeparator />
                <ActionMenuItem
                  destructive
                  icon={<Ban className="h-4 w-4" />}
                  onSelect={() => {
                    setCancelError(null);
                    setCancelling({ kind: 'order', row });
                  }}
                >
                  {tr('lab', 'cancel_whole_order')}
                </ActionMenuItem>
              </>
            )}
          </ActionMenu>
        )}
      </div>
    );
  };

  const selectAllHeader = allSelected ? '☑' : '☐';
  const loadMoreFooter = worklist.hasNextPage ? (
    <div className="flex w-full justify-center">
      <Button variant="outline" size="sm" onClick={() => void worklist.fetchNextPage()} loading={worklist.isFetchingNextPage}>
        {tr('lab', 'load_more')}
      </Button>
    </div>
  ) : undefined;

  return (
    <>
      <PageHeader
        title={tr('lab', 'laboratory')}
        description={tr('lab', 'collection_bench_work_verification_and_release')}
        actions={
          <div className="flex flex-wrap gap-2">
            {selected.length > 0 && tab === 'collect' && can('lab.collect') && (
              <Button onClick={() => startCollect(selected)} loading={collect.isPending}>
                {tr('lab', 'collect')} {selected.length}
              </Button>
            )}
            {selected.length > 0 && tab !== 'collect' && can('lab.collect', 'lab.result.enter') && (
              <Button variant="outline" onClick={() => setLabelling(labelTargets(selectedRows))}>
                <Printer className="me-1 h-4 w-4" /> {tr('lab', 'print')} {selected.length} {tr('lab', 'label_s')}
              </Button>
            )}
            {selected.length > 0 && tab === 'collected' && can('lab.collect', 'lab.result.enter') && (
              <Button onClick={() => receive.mutate({ orderItemIds: selected })} loading={receive.isPending}>
                {tr('lab', 'receive')} {selected.length} {tr('lab', 'into_the_lab')}
              </Button>
            )}
            {selected.length > 0 && tab === 'bench' && can('lab.result.enter') && (
              <Button variant="outline" onClick={() => setSendingOut(selected)}>
                {tr('lab', 'send_out')} {selected.length}
              </Button>
            )}
            {selected.length > 0 && tab === 'verify' && can('lab.result.verify') && selectedRows.every((row) => row.test.departmentGroup === 'microbiology') && (
              <Button variant="outline" onClick={() => verify.mutate({ ids: selected, action: 'preliminary' })}>
                {tr('lab', 'release_as_preliminary')}
              </Button>
            )}
            {selected.length > 0 && REJECT_TABS.has(tab) && can('lab.collect', 'lab.result.enter') && (
              <Button variant="ghost" onClick={() => setRejecting(selected)}>
                {tr('lab', 'reject')} {selected.length}
              </Button>
            )}
            {selected.length > 0 && tab === 'verify' && can('lab.result.verify') && (
              <>
                <Button variant="outline" onClick={() => verify.mutate({ ids: selected, action: 'verify' })} loading={verify.isPending && verify.variables?.action === 'verify'}>
                  {tr('lab', 'verify')} {selected.length}
                </Button>
                <Button onClick={() => verify.mutate({ ids: selected, action: 'release' })} loading={verify.isPending && verify.variables?.action === 'release' && !overriding}>
                  {tr('lab', 'verify_and_release')} {selected.length}
                </Button>
                <Button variant="ghost" onClick={() => setSendingBack(selected)}>
                  {tr('lab', 'send_back_to_bench')}
                </Button>
              </>
            )}
            {selected.length > 0 && tab === 'verified' && can('lab.result.verify') && (
              <>
                <Button onClick={() => verify.mutate({ ids: selected, action: 'release' })} loading={verify.isPending && !overriding}>
                  {tr('lab', 'release')} {selected.length}
                </Button>
                <Button variant="ghost" onClick={() => setSendingBack(selected)}>
                  {tr('lab', 'send_back_to_bench')}
                </Button>
              </>
            )}
            {can('order.write', 'lab.order.create', 'lab.collect') && (
              <Button onClick={() => navigate(homeCollectionOn ? '/lab/orders/new' : '/lab/orders/new?homeCollection=0')}>{tr('lab', 'order_tests')}</Button>
            )}
          </div>
        }
      />

      <Tabs
        active={tab}
        onChange={(next) => {
          setTab(next);
          setSelected([]);
        }}
        tabs={[
          { id: 'collect', label: tr('lab', 'to_collect') },
          { id: 'collected', label: tr('lab', 'collected_to_receive') },
          { id: 'bench', label: tr('lab', 'on_the_bench') },
          { id: 'verify', label: tr('lab', 'to_verify') },
          ...(showVerifiedTab ? [{ id: 'verified', label: tr('lab', 'verified_to_release') }] : []),
          { id: 'released', label: tr('lab', 'released') },
          ...(can('lab.result.edit.approve', 'lab.label.reprint.approve') ? [{ id: 'approvals', label: tr('lab', 'approvals') }] : []),
        ]}
      />

      {tab === 'approvals' ? (
        <LabApprovalsPage />
      ) : (
        <>

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('lab', 'name_mrn_order_no_phone_cnic_or_accession') }}
        filters={
          <>
            <FilterField label={tr('lab', 'department')}>
              <Select className="w-48" value={group} onChange={(e) => setGroup(e.target.value)} aria-label={tr('lab', 'department')}>
                <option value="">{tr('lab', 'all_departments')}</option>
                {GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {humanize(g)}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('lab', 'where_from')}>
              <Select className="w-36" value={source} onChange={(e) => setSource(e.target.value)} aria-label={tr('lab', 'where_from')}>
                <option value="">{tr('lab', 'all_sources')}</option>
                {Object.entries(SOURCE_LABELS)
                  .filter(([value]) => value !== 'home' || homeCollectionOn)
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            </FilterField>
            {(locations.data?.branches.length ?? 0) > 1 && (
              <FilterField label={tr('lab', 'branch')}>
                <Select
                  className="w-44"
                  value={branchId}
                  onChange={(e) => {
                    setBranchId(e.target.value);
                    setWardId('');
                  }}
                  aria-label={tr('lab', 'branch')}
                >
                  <option value="">{tr('lab', 'all_branches')}</option>
                  {locations.data?.branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
            {wards.length > 0 && (
              <FilterField label={tr('lab', 'ward')}>
                <Select className="w-44" value={wardId} onChange={(e) => setWardId(e.target.value)} aria-label={tr('lab', 'ward')}>
                  <option value="">{tr('lab', 'all_wards')}</option>
                  {wards.map((ward) => (
                    <option key={ward.id} value={ward.id}>
                      {ward.name}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
            <FilterField label={tr('lab', 'referring_doctor')}>
              <Input
                className="w-44"
                placeholder={tr('lab', 'referring_doctor')}
                aria-label={tr('lab', 'referring_doctor')}
                value={referrerDraft}
                onChange={(e) => setReferrerDraft(e.target.value)}
                onBlur={commitReferrer}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitReferrer();
                  }
                }}
              />
            </FilterField>
            <FilterField><div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {homeCollectionOn && <Checkbox checked={homeOnly} onCheckedChange={setHomeOnly} label={tr('lab', 'home_collection')} />}
              {tab === 'collect' && <Checkbox checked={dueNow} onCheckedChange={setDueNow} label={tr('lab', 'due_now')} />}
              {tab === 'verify' && <Checkbox checked={onlyNormal} onCheckedChange={setOnlyNormal} label={tr('lab', 'only_normal_results')} />}
              {tab === 'bench' && <Checkbox checked={awaitingReference} onCheckedChange={setAwaitingReference} label={tr('lab', 'awaiting_reference_lab')} />}
            </div></FilterField>
          </>
        }
        trailing={
          tab === 'collect' && can('lab.collect', 'lab.order.read') ? (
            <Button variant="outline" onClick={() => void printCollectionList()}>
              <Printer className="me-1 h-4 w-4" /> {tr('lab', 'collection_list')}
            </Button>
          ) : undefined
        }
        onReset={filtersActive ? resetFilters : undefined}
      />

      {tab === 'collected' && can('lab.collect', 'lab.result.enter') && (
        <form
          className="mb-3 flex max-w-md items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const scanned = scan.trim();
            if (scanned.length >= 3) receive.mutate({ accessionNo: scanned });
          }}
        >
          <ScanLine className="h-4 w-4 text-muted-foreground" aria-hidden />
          <Input autoFocus placeholder={tr('lab', 'scan_a_tube_to_receive_it')} aria-label={tr('lab', 'scan_a_tube_to_receive_it')} value={scan} onChange={(e) => setScan(e.target.value)} />
          <Button type="submit" variant="outline" loading={receive.isPending} disabled={scan.trim().length < 3}>
            {tr('lab', 'receive')}
          </Button>
        </form>
      )}

      {tab === 'verify' && separateVerifier && rows.some(lockedForMe) && (
        <p className="mb-2 text-xs text-muted-foreground">{tr('lab', 'results_you_entered_are_greyed_out_a_colleague')}</p>
      )}

      {TUBE_TABS.has(tab) ? (
        <DataTable<TubeGroup<WorklistItem>>
          rows={tubes}
          loading={worklist.isLoading} refreshing={worklist.isFetching}
          error={worklist.error}
          rowKey={(tube) => tube.key}
          empty={tr('lab', 'nothing_in_this_stage')}
          footer={loadMoreFooter}
          columns={[
            {
              header: selectAllHeader,
              width: '40px',
              cell: (tube) => (
                <Checkbox
                  checked={tube.rows.every((row) => selected.includes(row.id))}
                  onCheckedChange={(checked) => toggle(tube.rows.map((row) => row.id), checked)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={tr('lab', 'select_x_tube_for_x', { specimen: tube.specimen, fullName: tube.rows[0].patient.fullName })}
                />
              ),
            },
            {
              header: tr('lab', 'patient'),
              cell: (tube) => (tube.firstOfPatient ? <PatientCell row={tube.rows[0]} location={tube.rows[0].location} /> : null),
            },
            {
              header: tr('lab', 'tube'),
              cell: (tube) => (
                <div>
                  <p className="font-medium">
                    {tube.specimen}
                    {tube.container ? ` · ${tube.container}` : ''}
                  </p>
                  {tube.accessionNo && <p className="font-mono text-xs">{tube.accessionNo}</p>}
                </div>
              ),
            },
            {
              header: tr('lab', 'tests'),
              cell: (tube) => <span className="text-sm">{tube.rows.map((row) => row.test.name).join(', ')}</span>,
            },
            {
              header: tr('lab', 'priority'),
              cell: (tube) => (
                <div className="flex flex-wrap items-center gap-1">
                  <PriorityBadges priority={tube.priority} statOverdue={tube.statOverdue} collectAt={tab === 'collect' ? tube.collectAt : null} />
                  {tube.rows.some((row) => row.unpaid) && (
                    <Badge variant="warning">{tube.rows.some((row) => row.awaitingPayment) ? tr('lab', 'awaiting_payment') : tr('lab', 'unpaid')}</Badge>
                  )}
                  {tube.rows.some((row) => row.awaitingInsurer) && <Badge variant="warning">{tr('lab', 'awaiting_insurer')}</Badge>}
                </div>
              ),
            },
            {
              header: '',
              align: 'right',
              cell: (tube) => actions(tube.rows[0], tube.rows.length > 1),
            },
          ]}
        />
      ) : (
        <DataTable
          rows={rows}
          loading={worklist.isLoading} refreshing={worklist.isFetching}
          error={worklist.error}
          rowKey={(row) => row.id}
          empty={tab === 'verify' && onlyNormal ? tr('lab', 'no_normal_results_waiting') : tr('lab', 'nothing_in_this_stage')}
          footer={loadMoreFooter}
          columns={[
            {
              header: selectAllHeader,
              width: '40px',
              cell: (row) => (
                <Checkbox
                  checked={selected.includes(row.id)}
                  disabled={lockedForMe(row)}
                  onCheckedChange={(checked) => toggle([row.id], checked)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={tr('lab', 'select_x', { name: row.test.name })}
                />
              ),
            },
            {
              header: tr('lab', 'patient'),
              sort: (row) => row.patient.fullName,
              cell: (row) => <PatientCell row={row} location={row.location} />,
            },
            {
              header: tr('lab', 'test'),
              sort: (row) => row.test.name,
              cell: (row) => (
                <div className={lockedForMe(row) ? 'opacity-50' : undefined}>
                  <p>{row.test.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.test.specimenType}
                    {row.test.containerType ? ` · ${row.test.containerType}` : ''}
                  </p>
                  {row.sentOutTo && <p className="text-xs text-muted-foreground">{tr('lab', 'sent_to')} {row.sentOutTo}</p>}
                </div>
              ),
            },
            {
              header: tr('lab', 'accession'),
              sort: (row) => row.accessionNo,
              cell: (row) => <span className="font-mono text-xs">{row.accessionNo ?? '—'}</span>,
            },
            {
              header: tr('lab', 'result'),
              cell: (row) =>
                row.latestResult ? (
                  <div>
                    <span className={row.latestResult.flag.startsWith('critical') ? 'font-semibold text-destructive' : row.latestResult.flag !== 'normal' ? 'font-medium text-warning' : ''}>
                      {row.latestResult.value} {row.latestResult.unit ?? ''}
                      {row.latestResult.flag !== 'normal' && (
                        <Badge variant={row.latestResult.flag.startsWith('critical') ? 'destructive' : 'warning'} className="ms-1">
                          {humanize(row.latestResult.flag)}
                        </Badge>
                      )}
                    </span>
                    {row.latestResult.isAmended && (
                      <p className="mt-0.5 max-w-[16rem] text-[11px] text-warning">
                        {tr('lab', 'amended_previously')} {row.latestResult.previousValue ?? '—'}
                        {row.latestResult.amendReason ? ` · ${row.latestResult.amendReason}` : ''}
                      </p>
                    )}
                  </div>
                ) : row.resultHidden ? (
                  <span className="text-xs text-muted-foreground">{tr('lab', 'awaiting_verification')}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                ),
            },
            {
              header: tr('lab', 'status'),
              sort: (row) => row.status,
              cell: (row) => (
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge status={row.status} />
                  <PriorityBadges priority={row.priority} overdue={row.overdue} statOverdue={row.statOverdue} />
                </div>
              ),
            },
            {
              header: '',
              align: 'right',
              cell: (row) => actions(row),
            },
          ]}
        />
      )}
        </>
      )}

      {labelling && labelling.length > 0 && <LabelPrintDialog labels={labelling} onClose={() => setLabelling(null)} />}

      {entry && (
        <OrderResultsSheet
          orderId={entry.orderId}
          focusItemId={entry.itemId}
          onClose={() => setEntry(null)}
          onSaved={() => {
            setEntry(null);
            refresh();
            void queryClient.invalidateQueries({ queryKey: ['lab-entry'] });
          }}
        />
      )}
      {overriding && (
        <LabBenchReasonDialog
          title={tr('lab', 'release_with_an_unpaid_bill')}
          description={tr('lab', 'the_bill_for_one_of_these_tests_is_not_fully')}
          label={tr('lab', 'supervisor_override_reason')}
          confirmLabel={tr('lab', 'release_anyway')}
          minLength={5}
          pending={verify.isPending}
          onSubmit={(reason) => verify.mutate({ ids: overriding.ids, action: overriding.action, overrideReason: reason })}
          onClose={() => setOverriding(null)}
        />
      )}
      {identifying && (
        <LabIdentityDialog
          patients={identifying.patients}
          identifiers={pages[0]?.identifiers ?? []}
          mayOverride={can('lab.collect.identity.override')}
          onClose={() => setIdentifying(null)}
          onDone={(identities) => collect.mutate({ ids: identifying.ids, identities })}
        />
      )}
      {payingOverride && (
        <LabBenchReasonDialog
          title={tr('lab', 'collect_before_payment')}
          description={tr('lab', 'the_bill_for_a_walk_in_test_on_this_collection')}
          label={tr('lab', 'supervisor_override_reason')}
          confirmLabel={tr('lab', 'collect_anyway')}
          minLength={5}
          pending={collect.isPending}
          onSubmit={(reason) => collect.mutate({ ...payingOverride, paymentOverrideReason: reason })}
          onClose={() => setPayingOverride(null)}
        />
      )}
      {sendingBack && (
        <LabBenchReasonDialog
          title={`Send ${sendingBack.length} result${sendingBack.length === 1 ? '' : 's'} back to the bench`}
          description={tr('lab', 'the_result_stays_on_record_the_test_goes_back')}
          label={tr('lab', 'why_is_it_going_back')}
          confirmLabel={tr('lab', 'send_back')}
          minLength={5}
          destructive
          pending={verify.isPending}
          onSubmit={(reason) => verify.mutate({ ids: sendingBack, action: 'send_back', comment: reason })}
          onClose={() => setSendingBack(null)}
        />
      )}
      {sendingOut && (
        <LabSendOutDialog
          itemIds={sendingOut}
          summary={[...new Set(rows.filter((row) => sendingOut.includes(row.id)).map((row) => row.test.name))].join(', ')}
          onClose={() => setSendingOut(null)}
          onSent={() => {
            toast.success(tr('lab', 'sent_to_the_reference_lab'));
            setSendingOut(null);
            setSelected([]);
            refresh();
          }}
        />
      )}
      {rejecting && (
        <LabRejectDialog
          itemIds={rejecting}
          summary={[...new Set(rows.filter((row) => rejecting.includes(row.id)).map((row) => row.patient.fullName))].join(', ')}
          onClose={() => setRejecting(null)}
          onRejected={(count) => {
            toast.success(`${count} specimen${count === 1 ? '' : 's'} rejected`, tr('lab', 'the_ward_or_the_ordering_doctor_has_been_told'));
            setRejecting(null);
            setSelected([]);
            refresh();
          }}
        />
      )}
      {cancelling && (
        <LabBenchReasonDialog
          title={cancelling.kind === 'item' ? tr('lab', 'cancel_x', { name: cancelling.row.test.name }) : tr('lab', 'cancel_order_x', { orderNumber: cancelling.row.orderNumber })}
          description={
            cancelling.kind === 'item'
              ? tr('lab', 'x_x_only_a_test_that_has_no_result_yet_can_be', { fullName: cancelling.row.patient.fullName, orderNumber: cancelling.row.orderNumber })
              : tr('lab', 'x_every_test_on_this_order_that_has_no_result', { fullName: cancelling.row.patient.fullName })
          }
          label={tr('lab', 'reason_for_cancelling')}
          confirmLabel={cancelling.kind === 'item' ? tr('lab', 'cancel_test') : tr('lab', 'cancel_whole_order')}
          minLength={3}
          destructive
          pending={cancel.isPending}
          error={cancelError}
          onSubmit={(reason) => {
            setCancelError(null);
            cancel.mutate({ target: cancelling, reason });
          }}
          onClose={() => setCancelling(null)}
        />
      )}
      {adding && (
        <LabBenchAddTestsDialog
          orderId={adding.labOrderId}
          orderNumber={adding.orderNumber}
          patientName={adding.patient.fullName}
          onClose={() => setAdding(null)}
          onAdded={() => {
            setAdding(null);
            refresh();
            void queryClient.invalidateQueries({ queryKey: ['lab-entry'] });
          }}
        />
      )}
    </>
  );
}
